// src/components/modals/EditDriverModal.jsx
import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { X, Save } from 'lucide-react';
import { updateDriver } from '../../store/driverSlice';
import { VEHICLE_TYPE, LICENSE_TYPE, DRIVER_STATUS } from '../../store/driverSlice';
import { 
  validateDriverData, 
  hasErrors,
  getFirstError 
} from '../../utils/driverValidation';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Textarea } from '../ui/Textarea';
import { Button } from '../ui/Button/Button';
import { useToast } from '../ui/Toast';

export function EditDriverModal({ isOpen, onClose, driver }) {
  const dispatch = useDispatch();
  const { showSuccess, showError } = useToast();
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [activeTab, setActiveTab] = useState('personal');
  
  const [formData, setFormData] = useState({
    personalInfo: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      dateOfBirth: '',
      address: '',
      emergencyContact: {
        name: '',
        phone: '',
        relationship: ''
      }
    },
    licenseInfo: {
      licenseNumber: '',
      licenseType: '',
      issueDate: '',
      expiryDate: '',
      issuingState: ''
    },
    vehicleInfo: {
      make: '',
      model: '',
      year: '',
      color: '',
      plateNumber: '',
      vehicleType: '',
      registrationNumber: '',
      insurancePolicyNumber: ''
    },
    bankingInfo: {
      accountHolderName: '',
      accountNumber: '',
      routingNumber: '',
      bankName: ''
    },
    status: ''
  });

  // Populate form data when driver changes
  useEffect(() => {
    if (driver) {
      setFormData({
        personalInfo: {
          firstName: driver.personalInfo?.firstName || '',
          lastName: driver.personalInfo?.lastName || '',
          email: driver.personalInfo?.email || driver.email || '',
          phone: driver.personalInfo?.phone || driver.phone || '',
          dateOfBirth: driver.personalInfo?.dateOfBirth || '',
          address: driver.personalInfo?.address || '',
          emergencyContact: {
            name: driver.personalInfo?.emergencyContact?.name || '',
            phone: driver.personalInfo?.emergencyContact?.phone || '',
            relationship: driver.personalInfo?.emergencyContact?.relationship || ''
          }
        },
        licenseInfo: {
          licenseNumber: driver.licenseInfo?.licenseNumber || '',
          licenseType: driver.licenseInfo?.licenseType || '',
          issueDate: driver.licenseInfo?.issueDate || '',
          expiryDate: driver.licenseInfo?.expiryDate || '',
          issuingState: driver.licenseInfo?.issuingState || ''
        },
        vehicleInfo: {
          make: driver.vehicleInfo?.make || '',
          model: driver.vehicleInfo?.model || '',
          year: driver.vehicleInfo?.year || '',
          color: driver.vehicleInfo?.color || '',
          plateNumber: driver.vehicleInfo?.plateNumber || '',
          vehicleType: driver.vehicleInfo?.vehicleType || '',
          registrationNumber: driver.vehicleInfo?.registrationNumber || '',
          insurancePolicyNumber: driver.vehicleInfo?.insurancePolicyNumber || ''
        },
        bankingInfo: {
          accountHolderName: driver.bankingInfo?.accountHolderName || '',
          accountNumber: driver.bankingInfo?.accountNumber || '',
          routingNumber: driver.bankingInfo?.routingNumber || '',
          bankName: driver.bankingInfo?.bankName || ''
        },
        status: driver.status || ''
      });
      setErrors({});
    }
  }, [driver]);

  if (!isOpen || !driver) return null;

  const handleInputChange = (section, field, value) => {
    if (field.includes('.')) {
      const [parentField, childField] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [section]: {
          ...prev[section],
          [parentField]: {
            ...prev[section][parentField],
            [childField]: value
          }
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: value
        }
      }));
    }
    
    // Clear error for this field
    if (errors[`${section}.${field}`]) {
      setErrors(prev => ({
        ...prev,
        [`${section}.${field}`]: null
      }));
    }
  };

  const handleStatusChange = (value) => {
    setFormData(prev => ({
      ...prev,
      status: value
    }));
  };

  const handleSubmit = async () => {
    // Validate all data
    const allErrors = validateDriverData(formData);
    setErrors(allErrors);
    
    if (hasErrors(allErrors)) {
      showError('Validation Error', getFirstError(allErrors));
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const result = await dispatch(updateDriver(driver.id, formData));
      
      if (result.success) {
        showSuccess('Success', 'Driver updated successfully');
        onClose();
      } else {
        showError('Error', result.error || 'Failed to update driver');
      }
    } catch (error) {
      showError('Error', 'An unexpected error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const tabs = [
    { id: 'personal', label: 'Personal Info' },
    { id: 'license', label: 'License Info' },
    { id: 'vehicle', label: 'Vehicle Info' },
    { id: 'banking', label: 'Banking Info' },
    { id: 'status', label: 'Status' }
  ];

  const renderTabNavigation = () => (
    <div className="border-b border-gray-200">
      <nav className="-mb-px flex space-x-8">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`
              py-2 px-1 border-b-2 font-medium text-sm transition-colors
              ${activeTab === tab.id
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }
            `}
          >
            {tab.label}
          </button>
        ))}
      </nav>
    </div>
  );

  const renderPersonalInfo = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <Input
          name="firstName"
          label="First Name"
          value={formData.personalInfo.firstName}
          onChange={(e) => handleInputChange('personalInfo', 'firstName', e.target.value)}
          error={errors['personalInfo.firstName']}
          required
        />
        <Input
          name="lastName"
          label="Last Name"
          value={formData.personalInfo.lastName}
          onChange={(e) => handleInputChange('personalInfo', 'lastName', e.target.value)}
          error={errors['personalInfo.lastName']}
          required
        />
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <Input
          name="email"
          type="email"
          label="Email Address"
          value={formData.personalInfo.email}
          onChange={(e) => handleInputChange('personalInfo', 'email', e.target.value)}
          error={errors['personalInfo.email']}
          required
        />
        <Input
          name="phone"
          type="tel"
          label="Phone Number"
          value={formData.personalInfo.phone}
          onChange={(e) => handleInputChange('personalInfo', 'phone', e.target.value)}
          error={errors['personalInfo.phone']}
          required
        />
      </div>
      
      <Input
        name="dateOfBirth"
        type="date"
        label="Date of Birth"
        value={formData.personalInfo.dateOfBirth}
        onChange={(e) => handleInputChange('personalInfo', 'dateOfBirth', e.target.value)}
        error={errors['personalInfo.dateOfBirth']}
        required
      />
      
      <Textarea
        name="address"
        label="Address"
        value={formData.personalInfo.address}
        onChange={(e) => handleInputChange('personalInfo', 'address', e.target.value)}
        error={errors['personalInfo.address']}
        rows={3}
        required
      />
      
      <div className="border-t pt-4">
        <h4 className="text-lg font-medium text-gray-900 mb-4">Emergency Contact</h4>
        <div className="space-y-4">
          <Input
            name="emergencyContactName"
            label="Contact Name"
            value={formData.personalInfo.emergencyContact.name}
            onChange={(e) => handleInputChange('personalInfo', 'emergencyContact.name', e.target.value)}
            error={errors['personalInfo.emergencyContact.name']}
            required
          />
          <div className="grid grid-cols-2 gap-4">
            <Input
              name="emergencyContactPhone"
              type="tel"
              label="Contact Phone"
              value={formData.personalInfo.emergencyContact.phone}
              onChange={(e) => handleInputChange('personalInfo', 'emergencyContact.phone', e.target.value)}
              error={errors['personalInfo.emergencyContact.phone']}
              required
            />
            <Input
              name="emergencyContactRelationship"
              label="Relationship"
              value={formData.personalInfo.emergencyContact.relationship}
              onChange={(e) => handleInputChange('personalInfo', 'emergencyContact.relationship', e.target.value)}
              error={errors['personalInfo.emergencyContact.relationship']}
              required
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderLicenseInfo = () => (
    <div className="space-y-4">
      <Input
        name="licenseNumber"
        label="License Number"
        value={formData.licenseInfo.licenseNumber}
        onChange={(e) => handleInputChange('licenseInfo', 'licenseNumber', e.target.value)}
        error={errors['licenseInfo.licenseNumber']}
        required
      />
      
      <Select
        name="licenseType"
        label="License Type"
        value={formData.licenseInfo.licenseType}
        onChange={(e) => handleInputChange('licenseInfo', 'licenseType', e.target.value)}
        options={Object.values(LICENSE_TYPE).map(type => ({ value: type, label: type.toUpperCase() }))}
        error={errors['licenseInfo.licenseType']}
        required
      />
      
      <div className="grid grid-cols-2 gap-4">
        <Input
          name="issueDate"
          type="date"
          label="Issue Date"
          value={formData.licenseInfo.issueDate}
          onChange={(e) => handleInputChange('licenseInfo', 'issueDate', e.target.value)}
          error={errors['licenseInfo.issueDate']}
          required
        />
        <Input
          name="expiryDate"
          type="date"
          label="Expiry Date"
          value={formData.licenseInfo.expiryDate}
          onChange={(e) => handleInputChange('licenseInfo', 'expiryDate', e.target.value)}
          error={errors['licenseInfo.expiryDate']}
          required
        />
      </div>
      
      <Input
        name="issuingState"
        label="Issuing State"
        value={formData.licenseInfo.issuingState}
        onChange={(e) => handleInputChange('licenseInfo', 'issuingState', e.target.value)}
        error={errors['licenseInfo.issuingState']}
        maxLength={2}
        placeholder="e.g., CA"
        required
      />
    </div>
  );

  const renderVehicleInfo = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <Input
          name="make"
          label="Vehicle Make"
          value={formData.vehicleInfo.make}
          onChange={(e) => handleInputChange('vehicleInfo', 'make', e.target.value)}
          error={errors['vehicleInfo.make']}
          required
        />
        <Input
          name="model"
          label="Vehicle Model"
          value={formData.vehicleInfo.model}
          onChange={(e) => handleInputChange('vehicleInfo', 'model', e.target.value)}
          error={errors['vehicleInfo.model']}
          required
        />
      </div>

      <div className="grid grid-cols-3 gap-4">
        <Input
          name="year"
          type="number"
          label="Year"
          value={formData.vehicleInfo.year}
          onChange={(e) => handleInputChange('vehicleInfo', 'year', e.target.value)}
          error={errors['vehicleInfo.year']}
          min="2000"
          max={new Date().getFullYear() + 1}
          required
        />
        <Input
          name="color"
          label="Color"
          value={formData.vehicleInfo.color}
          onChange={(e) => handleInputChange('vehicleInfo', 'color', e.target.value)}
          error={errors['vehicleInfo.color']}
          required
        />
        <Input
          name="plateNumber"
          label="Plate Number"
          value={formData.vehicleInfo.plateNumber}
          onChange={(e) => handleInputChange('vehicleInfo', 'plateNumber', e.target.value)}
          error={errors['vehicleInfo.plateNumber']}
          required
        />
      </div>

      <Select
        name="vehicleType"
        label="Vehicle Type"
        value={formData.vehicleInfo.vehicleType}
        onChange={(e) => handleInputChange('vehicleInfo', 'vehicleType', e.target.value)}
        options={Object.values(VEHICLE_TYPE).map(type => ({ value: type, label: type.charAt(0).toUpperCase() + type.slice(1) }))}
        error={errors['vehicleInfo.vehicleType']}
        required
      />

      <div className="grid grid-cols-2 gap-4">
        <Input
          name="registrationNumber"
          label="Registration Number"
          value={formData.vehicleInfo.registrationNumber}
          onChange={(e) => handleInputChange('vehicleInfo', 'registrationNumber', e.target.value)}
          error={errors['vehicleInfo.registrationNumber']}
          required
        />
        <Input
          name="insurancePolicyNumber"
          label="Insurance Policy Number"
          value={formData.vehicleInfo.insurancePolicyNumber}
          onChange={(e) => handleInputChange('vehicleInfo', 'insurancePolicyNumber', e.target.value)}
          error={errors['vehicleInfo.insurancePolicyNumber']}
          required
        />
      </div>
    </div>
  );

  const renderBankingInfo = () => (
    <div className="space-y-4">
      <Input
        name="accountHolderName"
        label="Account Holder Name"
        value={formData.bankingInfo.accountHolderName}
        onChange={(e) => handleInputChange('bankingInfo', 'accountHolderName', e.target.value)}
        error={errors['bankingInfo.accountHolderName']}
        required
      />

      <div className="grid grid-cols-2 gap-4">
        <Input
          name="accountNumber"
          label="Account Number"
          value={formData.bankingInfo.accountNumber}
          onChange={(e) => handleInputChange('bankingInfo', 'accountNumber', e.target.value)}
          error={errors['bankingInfo.accountNumber']}
          required
        />
        <Input
          name="routingNumber"
          label="Routing Number"
          value={formData.bankingInfo.routingNumber}
          onChange={(e) => handleInputChange('bankingInfo', 'routingNumber', e.target.value)}
          error={errors['bankingInfo.routingNumber']}
          required
        />
      </div>

      <Input
        name="bankName"
        label="Bank Name"
        value={formData.bankingInfo.bankName}
        onChange={(e) => handleInputChange('bankingInfo', 'bankName', e.target.value)}
        error={errors['bankingInfo.bankName']}
        required
      />
    </div>
  );

  const renderStatusInfo = () => (
    <div className="space-y-4">
      <Select
        name="status"
        label="Driver Status"
        value={formData.status}
        onChange={(e) => handleStatusChange(e.target.value)}
        options={Object.values(DRIVER_STATUS).map(status => ({
          value: status,
          label: status.charAt(0).toUpperCase() + status.slice(1)
        }))}
        required
      />

      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-yellow-800">Status Change Warning</h3>
            <div className="mt-2 text-sm text-yellow-700">
              <p>Changing the driver status will affect their ability to receive trips and access the platform. Please ensure this change is intentional.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Edit Driver</h2>
            <p className="text-sm text-gray-600">
              {driver.personalInfo?.firstName || driver.name} {driver.personalInfo?.lastName || ''}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="px-6 pt-4">
          {renderTabNavigation()}
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {activeTab === 'personal' && renderPersonalInfo()}
          {activeTab === 'license' && renderLicenseInfo()}
          {activeTab === 'vehicle' && renderVehicleInfo()}
          {activeTab === 'banking' && renderBankingInfo()}
          {activeTab === 'status' && renderStatusInfo()}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
          <Button
            variant="outline"
            onClick={onClose}
          >
            Cancel
          </Button>
          
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="flex items-center gap-2"
          >
            <Save size={16} />
            {isSubmitting ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>
    </div>
  );
}
